// Phase 13 – White Label: Master Settings, Org Branding + Approval Workflow,
// Domains (subdomain + custom domain + DNS instructions), Login Branding,
// Landing Page, Policy Pages, Certificate Branding, WL Dashboard.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Palette, Globe, Shield, FileText, Award, CheckCircle2, XCircle,
  Plus, Trash2, Copy, Sparkles, Building2, Send, Eye,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

const BRAND_MODES = [
  ["steward_only", "Steward Only"],
  ["co_branded", "Co-Branded (default)"],
  ["org_primary", "Organization Primary"],
  ["full_white_label", "Full White Label"],
];

// ============================================================
// SUPER ADMIN — WL DASHBOARD
// ============================================================

export function AdminWLDashboardPage() {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/white-label/dashboard"); setD(data.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!d) return <DashboardLayout title="White Label"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="White Label Dashboard" breadcrumbs={["Admin", "White Label"]}>
      <div className="grid gap-4 md:grid-cols-3" data-testid="wl-dashboard">
        <StatCard label="Organizations" value={d.total_organizations} icon={Building2} tint="navy" />
        <StatCard label="Active Branded Portals" value={d.active_branded_portals} icon={Palette} tint="green" />
        <StatCard label="Pending Approvals" value={d.pending_branding_approvals} icon={Sparkles} tint="amber" />
        <StatCard label="Active Domains" value={d.active_domains} icon={Globe} tint="navy" />
        <StatCard label="Pending Domains" value={d.pending_domains} icon={Globe} tint="amber" />
        <StatCard label="Suspended Domains" value={d.suspended_domains} icon={Shield} tint="rose" />
      </div>
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Branding modes in use</div>
        <div className="flex flex-wrap gap-2">
          {(d.by_branding_mode || []).map((m) => (
            <span key={m.mode} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700" data-testid={`wl-mode-${m.mode}`}>
              {m.mode}: <span className="font-bold">{m.count}</span>
            </span>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — MASTER SETTINGS
// ============================================================

export function AdminWLSettingsPage() {
  const [s, setS] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/white-label/settings"); setS(data.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const tog = (k) => setS({ ...s, [k]: !s[k] });
  const save = async () => {
    try { await api.put("/admin/white-label/settings", s); toast.success("Settings saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!s) return <DashboardLayout title="WL Settings"><LoadingBlock /></DashboardLayout>;
  const flags = [
    ["white_label_enabled", "White Label enabled globally"],
    ["allow_organization_logo", "Allow organization logo upload"],
    ["allow_organization_colors", "Allow custom brand colors"],
    ["allow_custom_login_page", "Allow custom login branding"],
    ["allow_custom_subdomain", "Allow custom subdomains"],
    ["allow_custom_domain", "Allow custom domains (CNAME)"],
    ["allow_custom_certificate_template", "Allow custom certificate branding"],
    ["allow_custom_email_branding", "Allow custom email branding"],
    ["allow_custom_footer", "Allow custom footer text"],
    ["allow_custom_policy_pages", "Allow custom policy pages"],
    ["require_admin_approval", "Require Super Admin approval before publish"],
  ];
  return (
    <DashboardLayout title="White Label — Master Settings" breadcrumbs={["Admin", "WL Settings"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-2xl space-y-2" data-testid="wl-settings">
        {flags.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer" data-testid={`wl-flag-${k}`}>
            <span className="text-sm">{l}</span>
            <input type="checkbox" checked={!!s[k]} onChange={() => tog(k)} className="h-4 w-4" />
          </label>
        ))}
        <div className="mt-3">
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Default branding mode</label>
          <select value={s.default_branding_mode} onChange={(e) => setS({ ...s, default_branding_mode: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="wl-default-mode">
            {BRAND_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <button type="button" onClick={save} data-testid="wl-settings-save"
                className="mt-3 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d1360]">
          Save master settings
        </button>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — BRANDING APPROVAL QUEUE
// ============================================================

export function AdminBrandingApprovalQueuePage() {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/branding-approval-queue"); setRows(data.data?.queue || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const approve = async (oid) => {
    try { await api.post(`/admin/organizations/${oid}/branding/approve`); toast.success("Approved"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const publish = async (oid) => {
    try { await api.post(`/admin/organizations/${oid}/branding/publish`); toast.success("Published"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const reject = async (oid) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    try { await api.post(`/admin/organizations/${oid}/branding/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Approvals"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Branding Approval Queue" breadcrumbs={["Admin", "Approvals"]}>
      <div className="space-y-3" data-testid="wl-approval-queue">
        {rows.length === 0 ? <EmptyState title="No pending approvals" description="All submissions have been reviewed." icon={Shield} /> :
         rows.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`wl-appr-${r.organization_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-slate-800">{r.organization_name}</div>
                <div className="text-[11px] text-slate-500">Mode: {r.branding_mode} · Primary: <span className="inline-block h-3 w-3 rounded align-middle" style={{ background: r.primary_color }}></span> {r.primary_color}</div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => approve(r.organization_id)} data-testid={`wl-approve-${r.organization_id}`}
                        className="rounded-lg bg-[#329112] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90">
                  <CheckCircle2 className="inline h-3.5 w-3.5 mr-1" />Approve
                </button>
                <button type="button" onClick={() => publish(r.organization_id)} data-testid={`wl-publish-${r.organization_id}`}
                        className="rounded-lg bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90">
                  Publish
                </button>
                <button type="button" onClick={() => reject(r.organization_id)} data-testid={`wl-reject-${r.organization_id}`}
                        className="rounded-lg border border-rose-300 text-rose-700 px-3 py-1.5 text-xs font-semibold hover:bg-rose-50">
                  <XCircle className="inline h-3.5 w-3.5 mr-1" />Reject
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — ALL DOMAINS
// ============================================================

export function AdminAllDomainsPage() {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/domains"); setRows(data.data?.domains || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const approve = async (r) => {
    try { await api.post(`/admin/organizations/${r.organization_id}/domains/${r.id}/approve`); toast.success("Domain activated"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const suspend = async (r) => {
    try { await api.post(`/admin/organizations/${r.organization_id}/domains/${r.id}/suspend`); toast.success("Domain suspended"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Domains"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="All Domains" breadcrumbs={["Admin", "Domains"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="wl-domains-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2">Organization</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Domain</th>
            <th className="text-left px-3 py-2">DNS</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No domains requested yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`wl-domain-${r.id}`}>
                <td className="px-3 py-2">{r.organization_name}</td>
                <td className="px-3 py-2">{r.domain_type}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.full_domain}</td>
                <td className="px-3 py-2 text-xs">{r.dns_verification_status}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.domain_status === "active" ? "bg-green-100 text-green-700" : r.domain_status === "suspended" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{r.domain_status}</span></td>
                <td className="px-3 py-2 text-right space-x-2">
                  {r.domain_status !== "active" && (
                    <button type="button" onClick={() => approve(r)} className="text-xs text-[#329112] font-semibold hover:underline" data-testid={`wl-approve-domain-${r.id}`}>Approve</button>
                  )}
                  {r.domain_status === "active" && (
                    <button type="button" onClick={() => suspend(r)} className="text-xs text-rose-600 font-semibold hover:underline" data-testid={`wl-suspend-domain-${r.id}`}>Suspend</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// HOSPITAL ADMIN — BRANDING EDITOR + PREVIEW
// ============================================================

export function OrgBrandingEditorPage() {
  const [b, setB] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/org/branding"); setB(data.data || {}); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/org/branding", b); toast.success("Branding saved (draft)"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const submit = async () => {
    try { await api.post("/org/branding/submit-approval"); toast.success("Submitted for approval"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!b) return <DashboardLayout title="Branding"><LoadingBlock /></DashboardLayout>;
  const status = b.approval_status || "draft";
  return (
    <DashboardLayout title="Organization Branding" breadcrumbs={["White Label", "Branding"]}>
      <div className="grid gap-4 lg:grid-cols-[1fr,340px]" data-testid="wl-branding-editor">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Branding Settings</div>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
              status === "published" ? "bg-green-100 text-green-700" :
              status === "approved" ? "bg-blue-100 text-blue-700" :
              status === "pending_approval" ? "bg-amber-100 text-amber-700" :
              status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`} data-testid="wl-branding-status">{status}</span>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Branding mode</label>
            <select value={b.branding_mode || "co_branded"} onChange={(e) => setB({ ...b, branding_mode: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="wl-brand-mode">
              {BRAND_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[["primary_color", "Primary"], ["secondary_color", "Secondary"], ["accent_color", "Accent"], ["background_color", "Background"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{l}</label>
                <div className="mt-1 flex gap-2 items-center">
                  <input type="color" value={b[k] || "#101876"} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="h-9 w-12 rounded" data-testid={`wl-color-${k}`} />
                  <input type="text" value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            ))}
          </div>
          {[["organization_logo", "Organization logo URL"], ["favicon", "Favicon URL"],
            ["login_background_image", "Login background image URL"],
            ["footer_text", "Footer text"], ["support_email", "Support email"],
            ["support_mobile", "Support mobile"], ["website_url", "Website URL"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{l}</label>
              <input type="text" value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })}
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`wl-input-${k}`} />
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={save} data-testid="wl-brand-save"
                    className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-300">Save draft</button>
            {(status === "draft" || status === "rejected") && (
              <button type="button" onClick={submit} data-testid="wl-brand-submit"
                      className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d1360]">
                <Send className="inline h-3.5 w-3.5 mr-1" />Submit for approval
              </button>
            )}
          </div>
        </div>
        {/* Preview */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-fit sticky top-4" data-testid="wl-preview">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1"><Eye className="h-3.5 w-3.5" />Preview</div>
          </div>
          <div style={{ background: b.background_color || "#ffffff", color: b.text_color || "#0f172a" }} className="p-4">
            <div className="rounded-lg p-4 mb-3" style={{ background: b.primary_color || "#101876", color: "#fff" }}>
              <div className="text-xs opacity-80">Portal Header</div>
              <div className="text-lg font-bold">Your Portal</div>
            </div>
            <div className="rounded-lg p-3 mb-2 border" style={{ borderColor: b.secondary_color || "#329112" }}>
              <div className="text-xs">Card / Widget</div>
              <div className="text-sm font-semibold" style={{ color: b.secondary_color || "#329112" }}>Sample stat</div>
            </div>
            <button className="rounded-lg px-4 py-2 text-sm font-semibold" style={{ background: b.secondary_color || "#329112", color: "#fff" }}>Primary CTA</button>
            <div className="mt-3 text-[10px] opacity-70">{b.footer_text || "© Your Organization"}</div>
          </div>
        </div>
      </div>
      {status === "rejected" && b.rejection_reason && (
        <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700" data-testid="wl-rejection-reason">
          <strong>Rejection reason:</strong> {b.rejection_reason}
        </div>
      )}
    </DashboardLayout>
  );
}

// ============================================================
// HOSPITAL ADMIN — DOMAINS
// ============================================================

export function OrgDomainsPage() {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("subdomain");
  const [prefix, setPrefix] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [dns, setDns] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/org/domains"); setRows(data.data?.domains || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const request = async () => {
    try {
      await api.post("/org/domains/request", {
        domain_type: type,
        subdomain_prefix: type === "subdomain" ? prefix.trim().toLowerCase() : "",
        custom_domain: type === "custom_domain" ? customDomain.trim().toLowerCase() : "",
      });
      toast.success("Domain requested");
      setPrefix(""); setCustomDomain(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const showDNS = async (id) => {
    try { const { data } = await api.get(`/org/domains/${id}/dns-instructions`); setDns(data.data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const verify = async (id) => {
    try { await api.post(`/org/domains/${id}/verify`); toast.success("Verification triggered"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  if (rows === null) return <DashboardLayout title="Domains"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Custom Domains" breadcrumbs={["White Label", "Domains"]}>
      <div className="grid gap-4 lg:grid-cols-[1fr,340px]" data-testid="wl-org-domains">
        <div className="space-y-3">
          {rows.length === 0 ? <EmptyState title="No domains yet" description="Request a subdomain or custom domain." icon={Globe} /> :
           rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`wl-org-domain-${r.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-800 font-mono">{r.full_domain}</div>
                  <div className="text-[11px] text-slate-500">{r.domain_type} · DNS: {r.dns_verification_status} · SSL: {r.ssl_status}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.domain_status === "active" ? "bg-green-100 text-green-700" : r.domain_status === "suspended" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{r.domain_status}</span>
              </div>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => showDNS(r.id)} className="text-xs text-[#101876] hover:underline font-semibold" data-testid={`wl-dns-${r.id}`}>DNS instructions</button>
                {r.domain_type === "custom_domain" && r.dns_verification_status !== "verified" && (
                  <button type="button" onClick={() => verify(r.id)} className="text-xs text-[#329112] hover:underline font-semibold" data-testid={`wl-verify-${r.id}`}>Verify DNS</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 h-fit sticky top-4" data-testid="wl-domain-request">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Request a domain</div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="wl-domain-type">
            <option value="subdomain">Subdomain (free)</option>
            <option value="custom_domain">Custom Domain</option>
          </select>
          {type === "subdomain" ? (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Subdomain</label>
              <div className="flex items-center gap-1">
                <input type="text" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="hospitalname" data-testid="wl-domain-prefix"
                       className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
                <span className="text-xs text-slate-500">.stewardacademy.in</span>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Custom domain</label>
              <input type="text" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="learning.hospital.com" data-testid="wl-custom-domain"
                     className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
            </div>
          )}
          <button type="button" onClick={request} data-testid="wl-domain-request-btn"
                  className="w-full rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d1360]">
            <Plus className="inline h-3.5 w-3.5 mr-1" />Request
          </button>
        </div>
      </div>
      {dns && (
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="wl-dns-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">DNS Instructions — {dns.domain}</div>
            <button type="button" onClick={() => setDns(null)} className="text-xs text-slate-500 hover:underline">Close</button>
          </div>
          {dns.type === "subdomain" ? (
            <div className="text-sm text-slate-700">{dns.instructions}</div>
          ) : (
            <div className="space-y-3">
              {[["TXT (verification)", dns.txt_record], ["CNAME (routing)", dns.cname_record]].map(([label, rec]) => (
                <div key={label} className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
                  <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                    <div><span className="text-slate-400">Type</span><br />{rec.record_type}</div>
                    <div><span className="text-slate-400">Host</span><br />{rec.host}</div>
                    <div className="flex items-center gap-1"><span className="truncate"><span className="text-slate-400">Value</span><br />{rec.value}</span>
                      <button type="button" onClick={() => copy(rec.value)}><Copy className="h-3 w-3 text-slate-500" /></button>
                    </div>
                  </div>
                </div>
              ))}
              <ol className="ml-4 list-decimal text-xs text-slate-600 space-y-1">
                {(dns.steps || []).map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}

// ============================================================
// HOSPITAL ADMIN — LOGIN BRANDING
// ============================================================

export function OrgLoginBrandingPage() {
  const [b, setB] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/org/login-branding"); setB(data.data || {}); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const save = async () => {
    try { await api.put("/org/login-branding", b); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!b) return <DashboardLayout title="Login Branding"><LoadingBlock /></DashboardLayout>;
  const fields = [
    ["login_title", "Login page title"], ["login_subtitle", "Login page subtitle"],
    ["welcome_message", "Welcome message"], ["background_image", "Background image URL"],
    ["left_panel_image", "Left panel illustration URL"], ["support_text", "Support text"],
    ["footer_text", "Footer text"],
  ];
  return (
    <DashboardLayout title="Login Page Branding" breadcrumbs={["White Label", "Login"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-2xl space-y-3" data-testid="wl-login-branding">
        {fields.map(([k, l]) => (
          <div key={k}>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{l}</label>
            <input type="text" value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })}
                   className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`wl-lb-${k}`} />
          </div>
        ))}
        {[["show_steward_branding", "Show Steward branding"], ["show_powered_by", "Show 'Powered by Steward'"]].map(([k, l]) => (
          <label key={k} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer">
            <span className="text-sm">{l}</span>
            <input type="checkbox" checked={!!b[k]} onChange={() => setB({ ...b, [k]: !b[k] })} className="h-4 w-4" data-testid={`wl-lb-${k}`} />
          </label>
        ))}
        <button type="button" onClick={save} data-testid="wl-login-save"
                className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d1360]">Save login branding</button>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// HOSPITAL ADMIN — POLICY PAGES
// ============================================================

export function OrgPolicyPagesPage() {
  const [rows, setRows] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/org/policy-pages"); setRows(data.data?.pages || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const save = async (payload) => {
    try {
      if (editing) await api.put(`/org/policy-pages/${editing.id}`, payload);
      else await api.post("/org/policy-pages", payload);
      toast.success("Saved"); setShowForm(false); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Policies"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Policy Pages" breadcrumbs={["White Label", "Policies"]}>
      <div className="mb-3">
        <button type="button" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="wl-policy-new"
                className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d1360]">
          <Plus className="inline h-3.5 w-3.5 mr-1" />New policy page
        </button>
      </div>
      <div className="space-y-2" data-testid="wl-policies-list">
        {rows.length === 0 ? <EmptyState title="No policy pages" description="Create Privacy, Terms, Refund pages." icon={FileText} /> :
         rows.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid={`wl-policy-${r.page_slug}`}>
            <div>
              <div className="text-sm font-bold text-slate-800">{r.page_title}</div>
              <div className="text-[11px] text-slate-500">/{r.page_slug} · {r.page_type} · {r.status}</div>
            </div>
            <button type="button" onClick={() => { setEditing(r); setShowForm(true); }} className="text-xs text-[#101876] hover:underline font-semibold" data-testid={`wl-policy-edit-${r.page_slug}`}>Edit</button>
          </div>
        ))}
      </div>
      {showForm && <PolicyForm initial={editing} onCancel={() => { setShowForm(false); setEditing(null); }} onSave={save} />}
    </DashboardLayout>
  );
}

function PolicyForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState(initial || { page_title: "", page_slug: "", page_type: "privacy", page_content: "", status: "draft" });
  const submit = () => {
    if (!f.page_title || !f.page_slug || !f.page_content) return toast.error("Title, slug, content are required");
    onSave(f);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 space-y-3" data-testid="wl-policy-form">
        <div className="text-sm font-bold text-slate-800">{initial ? "Edit" : "Create"} policy page</div>
        <input type="text" placeholder="Page title" value={f.page_title} onChange={(e) => setF({ ...f, page_title: e.target.value })} data-testid="wl-policy-title" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input type="text" placeholder="page-slug (url friendly)" value={f.page_slug} onChange={(e) => setF({ ...f, page_slug: e.target.value.toLowerCase().replace(/\s/g, "-") })} data-testid="wl-policy-slug" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
        <select value={f.page_type} onChange={(e) => setF({ ...f, page_type: e.target.value })} data-testid="wl-policy-type" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
          {["privacy", "terms", "refund", "disclaimer", "contact", "about", "support"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <textarea rows={10} placeholder="Page content (markdown / plain text)" value={f.page_content} onChange={(e) => setF({ ...f, page_content: e.target.value })} data-testid="wl-policy-content" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono" />
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="wl-policy-status" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <option value="draft">Draft</option><option value="published">Published</option>
        </select>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold">Cancel</button>
          <button type="button" onClick={submit} data-testid="wl-policy-save" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white">Save</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HOSPITAL ADMIN — CERTIFICATE BRANDING
// ============================================================

export function OrgCertificateBrandingPage() {
  const [b, setB] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/org/certificate-branding"); setB(data.data || {}); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const save = async () => {
    try { await api.put("/org/certificate-branding", b); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!b) return <DashboardLayout title="Cert Branding"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Certificate Branding" breadcrumbs={["White Label", "Certificate"]}>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 max-w-2xl space-y-3" data-testid="wl-cert-branding">
        {[["certificate_logo", "Certificate logo URL"], ["certificate_seal", "Seal image URL"],
          ["signature_image", "Signatory signature image URL"], ["signatory_name", "Signatory name"],
          ["signatory_designation", "Signatory designation"], ["certificate_footer", "Certificate footer text"]].map(([k, l]) => (
          <div key={k}>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{l}</label>
            <input type="text" value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })}
                   className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`wl-cert-${k}`} />
          </div>
        ))}
        {[["co_branded_enabled", "Co-branded certificate (Steward + Org)"],
          ["steward_logo_visible", "Show Steward logo on certificate"],
          ["organization_logo_visible", "Show organization logo on certificate"],
          ["powered_by_visible", "Show 'Powered by Steward Learning Cloud'"]].map(([k, l]) => (
          <label key={k} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50 cursor-pointer">
            <span className="text-sm">{l}</span>
            <input type="checkbox" checked={!!b[k]} onChange={() => setB({ ...b, [k]: !b[k] })} className="h-4 w-4" data-testid={`wl-cert-flag-${k}`} />
          </label>
        ))}
        <button type="button" onClick={save} data-testid="wl-cert-save" className="rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0d1360]">
          <Award className="inline h-3.5 w-3.5 mr-1" />Save certificate branding
        </button>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// HOSPITAL ADMIN — STATUS HISTORY
// ============================================================

export function OrgBrandingHistoryPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/org/branding/status-history"); setRows(data.data?.history || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="History"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Branding Status History" breadcrumbs={["White Label", "History"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="wl-history">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-3 py-2">Timestamp</th>
            <th className="text-left px-3 py-2">From</th>
            <th className="text-left px-3 py-2">To</th>
            <th className="text-left px-3 py-2">Reason</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">No history yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-xs">{new Date(r.changed_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{r.old_status}</td>
                <td className="px-3 py-2 text-xs font-semibold">{r.new_status}</td>
                <td className="px-3 py-2 text-xs">{r.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
